import { useEffect, useState } from "react";
import { ArrowDown } from "lucide-react";
import { HERO, SITE } from "../../data/content";

export default function Hero() {
  const [active, setActive] = useState(0);
  const total = HERO.images.length;

  useEffect(() => {
    // Preload neighbours so the cross-fade looks instant
    HERO.images.forEach((src) => {
      const i = new Image();
      i.src = src;
    });
  }, []);

  useEffect(() => {
    if (total <= 1) return undefined;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % total);
    }, 6000);
    return () => clearInterval(id);
  }, [total]);

  const scrollToCottages = () => {
    document.getElementById("cottages")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[80vh] sm:min-h-[92vh] w-full flex items-end overflow-hidden"
    >
      {HERO.images.map((src, i) => (
        <div
          key={src}
          aria-hidden
          data-testid={`hero-slide-${i}`}
          className="hero-bg"
          style={{
            backgroundImage: `url('${src}')`,
            opacity: i === active ? 1 : 0,
            transition: "opacity 1.6s ease-in-out",
            zIndex: 0,
          }}
        />
      ))}
      <div className="hero-overlay" aria-hidden style={{ zIndex: 1 }} />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-20 sm:pb-28 pt-40">
        <div className="max-w-3xl">
          <p
            className="overline fade-up"
            style={{ color: "rgba(250,249,246,0.85)" }}
            data-testid="hero-eyebrow"
          >
            <span
              className="divider-rule mr-3"
              style={{ background: "rgba(250,249,246,0.85)" }}
            />
            {HERO.eyebrow}
          </p>

          <h1
            data-testid="hero-title"
            className="mt-6 font-serif text-[2.75rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight fade-up delay-1"
            style={{ color: "var(--dl-bg)", fontWeight: 400 }}
          >
            {HERO.title[0]}
            <br />
            <span className="serif-italic" style={{ color: "var(--dl-bg)" }}>
              {HERO.title[1]}
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed fade-up delay-2"
            style={{ color: "rgba(250,249,246,0.9)" }}
          >
            {HERO.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4 fade-up delay-3">
            <button
              data-testid="hero-explore-btn"
              onClick={scrollToCottages}
              className="btn-primary"
              style={{
                background: "var(--dl-bg)",
                color: "var(--dl-ink)",
                borderColor: "var(--dl-bg)",
              }}
            >
              Explore Stays
            </button>
            <a
              data-testid="hero-reserve-btn"
              href={SITE.bookingExternal}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
              style={{ borderColor: "var(--dl-bg)", color: "var(--dl-bg)" }}
            >
              Reserve Now
            </a>
          </div>
        </div>

        {total > 1 && (
          <div
            className="mt-12 flex items-center gap-2"
            data-testid="hero-dots"
          >
            {HERO.images.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Show slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className="transition-all duration-500"
                style={{
                  width: i === active ? 28 : 8,
                  height: 2,
                  background:
                    i === active
                      ? "var(--dl-bg)"
                      : "rgba(250,249,246,0.5)",
                }}
              />
            ))}
          </div>
        )}
      </div>

      <button
        onClick={scrollToCottages}
        className="hidden md:flex absolute bottom-8 right-12 z-10 items-center gap-3 text-[0.7rem] tracking-[0.3em] uppercase"
        style={{ color: "rgba(250,249,246,0.85)" }}
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        Scroll
        <ArrowDown size={14} />
      </button>
    </section>
  );
}
